import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';
import type {
	AddressData,
	Address,
	Addresses
} from '~~/core/entities/Addresses';
import lunr from 'lunr';
import { normalizeString } from '@/utils/index';

/*
    TODO: Escrever testes unitários para este arquivo o quanto antes
*/

interface LunrSearchResult {
	ref: number;
	score: number;
	matchData: {
		metadata: {
			[term: string]: {
				[field: string]: {
					position: [number, number][];
				};
			};
		};
	};
}

interface MappedLunrSearchResult extends AddressData {
	score: number;
}

const DEFAULT_ADDRESSES: Addresses[] = [];

async function getAddresses() {
	try {
		const [result, error] = await nuxtPropertiesRepository.getAddresses();

		if (error || !result) {
			return DEFAULT_ADDRESSES;
		}

		return result.data;
	} catch {
		return DEFAULT_ADDRESSES;
	}
}

export interface UseAddressesReturn {
	addresses: Ref<Addresses[]>;
	state: ComputedRef<string>;
	macroregions: ComputedRef<Address[]>;
	cities: ComputedRef<Address[]>;
	regions: ComputedRef<AddressData[]>;
	filteredRegions: ComputedRef<MappedLunrSearchResult[]>;
	filteredCities: ComputedRef<Address[]>;
}

export function useAddresses(searchTerm?: Ref<string>): UseAddressesReturn {
	const addresses = useState<Addresses[]>('addresses', () => DEFAULT_ADDRESSES);

	const addressesFetch = useAsyncData<Addresses[]>(
		`properties-addresses`,
		async () => {
			if (addresses.value.length) return addresses.value;

			const newAddresses = await getAddresses();

			return newAddresses;
		},
		{
			default: () => DEFAULT_ADDRESSES
		}
	);

	watchEffect(() => {
		addresses.value = addressesFetch.data.value || DEFAULT_ADDRESSES;
	});

	const state = computed<string>(() => addresses.value[0]?.state || '');
	const macroregions = computed<Address[]>(
		() => addresses.value[0]?.macroregions || []
	);
	const cities = computed<Address[]>(() => addresses.value[0]?.cities || []);
	const regions = computed<AddressData[]>(
		() => addresses.value[0]?.regions || []
	);

	const regionsIndex = ref<lunr.Index>();
	const citiesIndex = ref<lunr.Index>();

	watch(
		regions,
		() => {
			const documents =
				regions.value.map((region, idx) => ({
					id: idx,
					label: normalizeString(region.label),
					city: normalizeString(region.data.city),
					macroregion: normalizeString(region.data.macroregion)
				})) || [];

			regionsIndex.value = lunr(function (this: lunr.Builder) {
				this.ref('id');
				this.field('label', { boost: 10 });
				this.field('city', { boost: 5 });
				this.field('macroregion', { boost: 4 });
				this.pipeline.remove(lunr.stemmer);
				documents.forEach(doc => this.add(doc));
			});
		},
		{ immediate: true }
	);

	const filteredRegions = computed(() =>
		regionsIndex.value
			.search(
				searchTerm?.value ? `*${normalizeString(searchTerm?.value)}*` : ''
			)
			.map((result: LunrSearchResult) => ({
				...regions.value[result.ref],
				score: result.score
			}))
			.sort((a: MappedLunrSearchResult, b: MappedLunrSearchResult) => {
				if (b.score !== a.score) return b.score - a.score;

				return a.label.localeCompare(b.label);
			})
	);

	watch(
		cities,
		() => {
			const documents =
				cities.value.map((city, idx) => ({
					id: idx,
					label: normalizeString(city.label)
				})) || [];

			citiesIndex.value = lunr(function (this: lunr.Builder) {
				this.ref('id');
				this.field('label');
				this.pipeline.remove(lunr.stemmer);
				documents.forEach(doc => this.add(doc));
			});
		},
		{ immediate: true }
	);

	const filteredCities = computed(() =>
		citiesIndex.value
			.search(`*${searchTerm?.value || ''}*`)
			.map((result: LunrSearchResult) => {
				return cities.value[result.ref];
			})
			.sort((a: MappedLunrSearchResult, b: MappedLunrSearchResult) => {
				if (b.score !== a.score) return b.score - a.score;

				return a.label.localeCompare(b.label);
			})
	);

	return {
		addresses,
		state,
		macroregions,
		cities,
		regions,
		filteredRegions,
		filteredCities
	};
}
